import * as React from "react";

import { CollaborationBarLocation } from "./collaborationBarReducer";

interface CollaborationBarContext {
  /** Whether the collaboration bar is open (expanded). */
  isOpen: boolean;

  /**
   * Where the bar is pointed — its address. Meaningful whether the bar is
   * expanded or collapsed; closing remembers it rather than clearing it.
   */
  location: CollaborationBarLocation;

  /**
   * Expand the bar. Omit ``location`` to land where it left off; pass one to
   * navigate there in the same transition.
   *
   * This is how anywhere in the app hands a thread to the chat sidebar:
   * ``open({ mode: "ai-agent-chat", threadId, view: "thread" })``. There is no
   * request to mail and nothing for the sidebar to adopt — the address changes
   * and the sidebar, a pure function of it, follows.
   */
  open: (location?: CollaborationBarLocation) => void;

  /** Close (collapse) the collaboration bar. */
  close: () => void;
}

/**
 * Context for the collaboration bar (right sidebar).
 *
 * Use `useCollaborationBar` to access the context.
 */
export const CollaborationBarContext =
  React.createContext<CollaborationBarContext | null>(null);

/**
 * Custom hook to access the collaboration bar context.
 *
 * Usage:
 * ```typescript
 * const collaborationBar = useCollaborationBar();
 *
 * collaborationBar.location.threadId;
 * collaborationBar.open({ mode: "ai-agent-chat", threadId, view: "thread" });
 * collaborationBar.close();
 * ```
 */
export function useCollaborationBar(): CollaborationBarContext {
  const context = React.useContext(CollaborationBarContext);

  if (!context) {
    throw new Error(
      "useCollaborationBar must be used within CollaborationBarProvider",
    );
  }

  return context;
}
